import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Grid } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { colors, radii, shadows } from '../../styles/tokens';
import { trackEvent } from '../../services/analytics';
import { getRelatedTools } from '../Navigation/toolNavItems';

export interface RelatedToolsProps {
  currentPath: string;
  count?: number;
}

export const RelatedTools: React.FC<RelatedToolsProps> = ({ currentPath, count = 3 }) => {
  const navigate = useNavigate();
  const related = getRelatedTools(currentPath, count);

  // Tracked here rather than at each call site so every placement is covered:
  // this is the content-to-tool hop the intelligence section exists to produce.
  const goToTool = (path: string) => {
    trackEvent('related_tool_click', {
      tool_id: path.replace(/^\//, ''),
      source: currentPath.startsWith('/news') ? 'article' : 'tool',
    });
    navigate(path);
  };

  if (related.length === 0) return null;

  return (
    <Box component="section" sx={{ mt: 1 }}>
      <Typography
        variant="subtitle2"
        component="h2"
        sx={{ fontWeight: 700, color: colors.textPrimary, mb: 2 }}
      >
        Related tools
      </Typography>
      <Grid container spacing={2}>
        {related.map((tool) => (
          <Grid key={tool.path} size={{ xs: 12, sm: 6, md: 4 }}>
            <Box
              role="button"
              tabIndex={0}
              onClick={() => goToTool(tool.path)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  goToTool(tool.path);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                height: '100%',
                p: 2,
                cursor: 'pointer',
                bgcolor: colors.paper,
                border: `1px solid ${colors.border}`,
                borderRadius: `${radii.card}px`,
                boxShadow: shadows.resting,
                transition: 'box-shadow 150ms ease, border-color 150ms ease',
                '&:hover': {
                  boxShadow: shadows.hover,
                  borderColor: alpha(colors.primary, 0.35),
                },
                '&:focus-visible': {
                  outline: `2px solid ${colors.primary}`,
                  outlineOffset: 2,
                },
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  borderRadius: `${radii.button}px`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(colors.primary, 0.08),
                  color: colors.primary,
                }}
              >
                {tool.icon}
              </Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: colors.textPrimary, flex: 1, minWidth: 0 }}
              >
                {tool.text}
              </Typography>
              <ArrowForward sx={{ fontSize: 18, color: colors.textMuted }} />
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
